/**
 * Move Validation Logic
 *
 * Handles validation of Reversi moves including:
 * - Legal move checking
 * - Piece flipping calculation
 * - Valid move generation
 */

import {
  BoardPosition,
  PlayerColor,
  BOARD_SIZE,
  TOTAL_SQUARES,
  InvalidMoveError,
} from '@/types/game-types';
import { positionToCoords } from './board-utils';

/**
 * Validation result for a move attempt
 */
export interface MoveValidationResult {
  isValid: boolean;
  flippedPositions: BoardPosition[];
  reason?: string;
}

/**
 * Check if a move is legal and return flipped positions
 */
export const validateMove = (
  board: Uint8Array,
  position: BoardPosition,
  player: PlayerColor,
): MoveValidationResult => {
  // Position must be empty
  if (board[position] !== PlayerColor.EMPTY) {
    return {
      isValid: false,
      flippedPositions: [],
      reason: 'Position is already occupied',
    };
  }

  // Position must be on the board
  if (position < 0 || position >= TOTAL_SQUARES) {
    return {
      isValid: false,
      flippedPositions: [],
      reason: 'Position is outside the board',
    };
  }

  const flippedPositions = calculateFlips(board, position, player);

  if (flippedPositions.length === 0) {
    return {
      isValid: false,
      flippedPositions: [],
      reason: 'Move does not flip any opponent pieces',
    };
  }

  return {
    isValid: true,
    flippedPositions,
  };
};

/**
 * Calculate which pieces would be flipped by a move
 */
export const calculateFlips = (
  board: Uint8Array,
  position: BoardPosition,
  player: PlayerColor,
): BoardPosition[] => {
  const allFlips: BoardPosition[] = [];
  const opponent = player === PlayerColor.BLACK ? PlayerColor.WHITE : PlayerColor.BLACK;

  // All 8 directions: N, NE, E, SE, S, SW, W, NW
  const directions = [
    { row: -1, col: 0 },  // N
    { row: -1, col: 1 },  // NE
    { row: 0, col: 1 },   // E
    { row: 1, col: 1 },   // SE
    { row: 1, col: 0 },   // S
    { row: 1, col: -1 },  // SW
    { row: 0, col: -1 },  // W
    { row: -1, col: -1 }, // NW
  ];

  const startCoords = positionToCoords(position);

  for (const direction of directions) {
    const flipsInDirection = calculateFlipsInDirection(
      board,
      startCoords,
      direction,
      player,
      opponent,
    );
    allFlips.push(...flipsInDirection);
  }

  return allFlips;
};

/**
 * Calculate flips in a specific direction
 */
function calculateFlipsInDirection(
  board: Uint8Array,
  startCoords: { row: number; col: number },
  direction: { row: number; col: number },
  player: PlayerColor,
  opponent: PlayerColor,
): BoardPosition[] {
  const flips: BoardPosition[] = [];
  let currentRow = startCoords.row + direction.row;
  let currentCol = startCoords.col + direction.col;

  // First, collect all consecutive opponent pieces
  while (
    currentRow >= 0 && currentRow < BOARD_SIZE &&
    currentCol >= 0 && currentCol < BOARD_SIZE
  ) {
    const currentPosition = currentRow * BOARD_SIZE + currentCol;
    const currentPiece = board[currentPosition];

    if (currentPiece === opponent) {
      // Found opponent piece, add to potential flips
      flips.push(currentPosition);
    } else if (currentPiece === player) {
      // Found our piece - flips are valid if we have opponent pieces between
      return flips.length > 0 ? flips : [];
    } else {
      // Found empty square - no valid flips in this direction
      return [];
    }

    currentRow += direction.row;
    currentCol += direction.col;
  }

  // Reached edge of board without finding our piece - no valid flips
  return [];
}

/**
 * Get all valid moves for a player
 */
export const getValidMoves = (
  board: Uint8Array,
  player: PlayerColor,
): Map<BoardPosition, BoardPosition[]> => {
  const validMoves = new Map<BoardPosition, BoardPosition[]>();

  for (let position = 0; position < TOTAL_SQUARES; position++) {
    if (board[position] === PlayerColor.EMPTY) {
      const result = validateMove(board, position, player);
      if (result.isValid) {
        validMoves.set(position, result.flippedPositions);
      }
    }
  }

  return validMoves;
};

/**
 * Check if a player has any valid moves
 */
export const hasValidMoves = (board: Uint8Array, player: PlayerColor): boolean => {
  for (let position = 0; position < TOTAL_SQUARES; position++) {
    if (board[position] === PlayerColor.EMPTY) {
      const result = validateMove(board, position, player);
      if (result.isValid) {
        return true;
      }
    }
  }
  return false;
};

/**
 * Apply a move to the board (creates new board state)
 */
export const applyMove = (
  board: Uint8Array,
  position: BoardPosition,
  player: PlayerColor,
  flippedPositions: BoardPosition[],
): Uint8Array => {
  const newBoard = new Uint8Array(board);

  // Place the new piece
  newBoard[position] = player;

  // Flip all the captured pieces
  for (const flipPosition of flippedPositions) {
    newBoard[flipPosition] = player;
  }

  return newBoard;
};

/**
 * Validate and apply a move in one step
 */
export const makeMove = (
  board: Uint8Array,
  position: BoardPosition,
  player: PlayerColor,
): { newBoard: Uint8Array; flippedPositions: BoardPosition[] } => {
  const validation = validateMove(board, position, player);

  if (!validation.isValid) {
    throw new InvalidMoveError(
      validation.reason || 'Invalid move',
      position,
      'invalid_move',
    );
  }

  const newBoard = applyMove(board, position, player, validation.flippedPositions);

  return {
    newBoard,
    flippedPositions: validation.flippedPositions,
  };
};

/**
 * Check if game is over (no moves for either player)
 */
export const isGameOver = (board: Uint8Array): boolean => {
  return !hasValidMoves(board, PlayerColor.BLACK) && !hasValidMoves(board, PlayerColor.WHITE);
};

/**
 * Get game winner based on current board state
 */
export const getWinner = (board: Uint8Array): PlayerColor | 'tie' | null => {
  if (!isGameOver(board)) {
    return null; // Game not over yet
  }

  let blackCount = 0;
  let whiteCount = 0;

  for (let i = 0; i < TOTAL_SQUARES; i++) {
    if (board[i] === PlayerColor.BLACK) {
      blackCount++;
    } else if (board[i] === PlayerColor.WHITE) {
      whiteCount++;
    }
  }

  if (blackCount > whiteCount) {
    return PlayerColor.BLACK;
  } else if (whiteCount > blackCount) {
    return PlayerColor.WHITE;
  } else {
    return 'tie';
  }
};

/**
 * Check if a position is a corner move
 */
export const isCornerMove = (position: BoardPosition): boolean => {
  return [0, 7, 56, 63].includes(position);
};

/**
 * Check if a position is an edge move
 */
export const isEdgeMove = (position: BoardPosition): boolean => {
  const coords = positionToCoords(position);
  return coords.row === 0 || coords.row === 7 || coords.col === 0 || coords.col === 7;
};

/**
 * Check if a position is adjacent to a corner (dangerous X-square)
 */
export const isXSquareMove = (position: BoardPosition): boolean => {
  return [9, 14, 49, 54].includes(position);
};

/**
 * Check if a position is next to a corner (C-square)
 */
export const isCSquareMove = (position: BoardPosition): boolean => {
  return [1, 6, 8, 15, 48, 55, 57, 62].includes(position);
};

/**
 * Calculate mobility (number of valid moves) for a player after a hypothetical move
 */
export const calculateMobility = (
  board: Uint8Array,
  position: BoardPosition,
  player: PlayerColor,
): { playerMobility: number; opponentMobility: number } => {
  // Apply the hypothetical move
  const validation = validateMove(board, position, player);
  if (!validation.isValid) {
    return { playerMobility: 0, opponentMobility: 0 };
  }

  const newBoard = applyMove(board, position, player, validation.flippedPositions);
  const opponent = player === PlayerColor.BLACK ? PlayerColor.WHITE : PlayerColor.BLACK;

  const playerMoves = getValidMoves(newBoard, player);
  const opponentMoves = getValidMoves(newBoard, opponent);

  return {
    playerMobility: playerMoves.size,
    opponentMobility: opponentMoves.size,
  };
};

/**
 * Calculate stability score for a position (rough estimate)
 */
export const calculateStability = (
  board: Uint8Array,
  position: BoardPosition,
  player: PlayerColor,
): number => {
  let stability = 0;
  const coords = positionToCoords(position);

  // Corner pieces are completely stable
  if (isCornerMove(position)) {
    return 10;
  }

  // Edge pieces have some stability
  if (isEdgeMove(position)) {
    stability += 3;
  }

  // Check if piece is protected by friendly pieces in multiple directions
  const directions = [
    { row: -1, col: 0 }, { row: -1, col: 1 }, { row: 0, col: 1 }, { row: 1, col: 1 },
    { row: 1, col: 0 }, { row: 1, col: -1 }, { row: 0, col: -1 }, { row: -1, col: -1 },
  ];

  let protectedDirections = 0;
  for (const dir of directions) {
    const newRow = coords.row + dir.row;
    const newCol = coords.col + dir.col;

    if (newRow >= 0 && newRow < BOARD_SIZE && newCol >= 0 && newCol < BOARD_SIZE) {
      const neighborPos = newRow * BOARD_SIZE + newCol;
      if (board[neighborPos] === player) {
        protectedDirections++;
      }
    }
  }

  stability += Math.floor(protectedDirections / 2);
  return stability;
};